import { useState, useEffect } from 'react';
import { Heart, X, ShoppingBag, Star, Sparkles, Eye, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useWishlist } from '@/contexts/WishlistContext';
import { useCart } from '@/contexts/CartContext';
import { useNavigate } from 'react-router-dom';
import { PriceDisplay } from './PriceDisplay';
import { useLanguage } from '@/contexts/LanguageContext';

const Wishlist = ({ mobileNav = false }: { mobileNav?: boolean }) => {
  const { items, removeFromWishlist } = useWishlist();
  const { addToCart } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLanguage();

  // Handle browser back button to close wishlist
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      if (isOpen) {
        event.preventDefault();
        setIsOpen(false);
        // Push the state back to stay on the same page
        window.history.pushState(null, '', window.location.href);
      }
    };

    if (isOpen) {
      window.history.pushState({ wishlistOpen: true }, '');
      window.addEventListener('popstate', handlePopState);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen]);

  const handleAddToCart = (item: any) => {
    addToCart({
      id: item.id,
      name: item.name,
      brand: item.brand,
      price: item.price,
      image: item.image
    });
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="ghost" 
          size="icon" 
          className={mobileNav ? "flex-col h-auto py-2 px-6 gap-1.5 relative hover:bg-accent/60 active:scale-95 transition-all duration-200 rounded-xl min-w-[72px]" : "relative"}
          aria-label={t.wishlist.title}
        >
          <div className="relative">
            <Heart size={mobileNav ? 22 : 20} className="text-foreground/80" strokeWidth={2} />
            {items.length > 0 && (
              <span className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center border-2 border-background">
                {items.length}
              </span>
            )}
          </div>
          {mobileNav && <span className="text-[10px] font-medium text-foreground/70">{t.wishlist.title}</span>}
        </Button>
      </SheetTrigger>
      
      <SheetContent className="w-full sm:max-w-lg flex flex-col">
        <SheetHeader className="border-b pb-4">
          <SheetTitle className="flex items-center gap-2 text-xl">
            <Heart size={24} className="text-primary fill-current" />
            {t.wishlist.title}
            <Badge variant="secondary" className="ml-auto">
              {items.length} {items.length === 1 ? t.wishlist.item : t.wishlist.items}
            </Badge>
          </SheetTitle>
        </SheetHeader>
        
        <div className="flex flex-col flex-1">
          {items.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                  <Heart size={32} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{t.wishlist.empty}</h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {t.wishlist.emptyDescription}
                  </p>
                  <Button onClick={() => {
                    setIsOpen(false);
                    navigate('/', { state: { filterCategory: 'Visi' } });
                  }} variant="outline" className="gap-2">
                    <Sparkles size={16} />
                    {t.wishlist.discoverProducts}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Motivation banner */}
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-4 my-4">
                <div className="flex items-center gap-3">
                  <Star size={20} className="text-primary fill-current" />
                  <div className="flex-1">
                    <h3 className="font-medium text-sm">{t.wishlist.featuredTitle}</h3>
                    <p className="text-xs text-muted-foreground">
                      {t.wishlist.featuredDescription}
                    </p>
                  </div>
                </div>
              </div>

              {/* Products list */}
              <div className="flex-1 overflow-y-auto space-y-3 pr-2 max-h-[60vh]">
                {items.map((item, index) => (
                  <div key={item.id} className="bg-card rounded-lg border p-4 shadow-sm hover:shadow-md transition-shadow">
                    <div className="flex gap-3">
                      <div className="relative flex-shrink-0">
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg border cursor-pointer hover:opacity-90 transition-opacity"
                          onClick={() => {
                            setIsOpen(false);
                            navigate(`/produktas/${item.id}`);
                          }}
                        />
                        <div className="absolute -top-2 -left-2 bg-primary text-primary-foreground text-xs rounded-full w-6 h-6 flex items-center justify-center font-medium">
                          {index + 1}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1 min-w-0 pr-2">
                            <h4 className="font-semibold text-sm leading-tight truncate cursor-pointer hover:text-primary transition-colors" 
                                title={item.name}
                                onClick={() => {
                                  setIsOpen(false);
                                  navigate(`/produktas/${item.id}`);
                                }}>
                              {item.name}
                            </h4>
                            <p className="text-xs text-muted-foreground mt-1 truncate" title={item.brand}>
                              {item.brand}
                            </p>
                            <PriceDisplay 
                              price={item.price}
                              className="text-sm font-bold text-primary mt-1"
                              showLoginPrompt={false}
                            />
                          </div>
                          <Button
                            size="icon"
                            variant="ghost"
                            className="h-6 w-6 text-muted-foreground hover:text-destructive flex-shrink-0"
                            onClick={() => removeFromWishlist(item.id)}
                          >
                            <X size={12} />
                          </Button>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1 btn-hero text-xs font-medium"
                            onClick={() => handleAddToCart(item)}
                          >
                            <ShoppingBag size={14} className="mr-1" />
                            {t.wishlist.addToCart}
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            className="px-3"
                            onClick={() => {
                              setIsOpen(false);
                              navigate(`/produktas/${item.id}`);
                            }}
                          >
                            <Eye size={14} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Quick actions */}
              <div className="border-t pt-4 space-y-3">
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                  <span>{t.wishlist.totalProducts}</span>
                  <span className="font-medium">{items.length}</span>
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Button 
                    className="w-full btn-hero text-base font-semibold py-3"
                    onClick={() => {
                      // Add all items to cart
                      items.forEach(item => handleAddToCart(item));
                      setIsOpen(false);
                    }}
                    disabled={items.length === 0}
                  >
                    <ShoppingBag size={18} className="mr-2" />
                    {t.wishlist.addAllToCart}
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setIsOpen(false)}
                  >
                    <ArrowRight size={16} className="mr-2" />
                    {t.wishlist.continueShopping}
                  </Button>
                </div>

                {/* Trust indicator */}
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground pt-2">
                  <Heart size={12} className="fill-current" />
                  <span>{t.wishlist.savedToProfile}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default Wishlist;